import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  TextField,
  Grid,
  IconButton,
  Button,
  Typography,
  Tooltip,
  Paper,
  Divider,
} from "@material-ui/core";
import { Formik, Form } from "formik";
import { isArray } from "lodash";
import NumberFormat from "react-number-format";
import ButtonWithSpinner from "../ButtonWithSpinner";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  fullWidth: {
    width: "100%",
  },
  textfield: {
    width: "100%",
    fontSize: "0.875em",
  },
  dayCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    transition: "border-color 0.2s",
    "&:hover": {
      borderColor: theme.palette.primary.light,
    },
  },
  dayHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
  },
  dayName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
  },
  closedText: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontSize: "0.85rem",
    padding: theme.spacing(1, 0),
  },
  shiftRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  shiftLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    minWidth: 52,
    textAlign: "center",
  },
  addShiftBtn: {
    textTransform: "none",
    fontSize: "0.8rem",
    marginTop: theme.spacing(0.5),
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(0.5, 0),
  },
}));

const WEEKDAYS = [
  { key: "monday", label: () => i18n.t("queueModal.serviceHours.monday") },
  { key: "tuesday", label: () => i18n.t("queueModal.serviceHours.tuesday") },
  { key: "wednesday", label: () => i18n.t("queueModal.serviceHours.wednesday") },
  { key: "thursday", label: () => i18n.t("queueModal.serviceHours.thursday") },
  { key: "friday", label: () => i18n.t("queueModal.serviceHours.friday") },
  { key: "saturday", label: () => i18n.t("queueModal.serviceHours.saturday") },
  { key: "sunday", label: () => i18n.t("queueModal.serviceHours.sunday") },
];

const MAX_SHIFTS = 6;

/**
 * Converte formato legado (startTimeA/endTimeA/startTimeB/endTimeB)
 * para formato novo (shifts[]).
 */
const legacyToShifts = (schedule) => {
  if (schedule.shifts && Array.isArray(schedule.shifts)) {
    return schedule.shifts;
  }

  const shifts = [];

  if (schedule.startTimeA && schedule.endTimeA) {
    shifts.push({ start: schedule.startTimeA, end: schedule.endTimeA });
  }

  if (schedule.startTimeB && schedule.endTimeB) {
    shifts.push({ start: schedule.startTimeB, end: schedule.endTimeB });
  }

  return shifts;
};

/**
 * Converte formato novo (shifts[]) para formato legado + novo.
 * Mantém backward compat: sempre inclui startTimeA/B + shifts[].
 */
const shiftsToSchedule = (weekdayEn, weekday, shifts) => {
  const result = {
    weekday,
    weekdayEn,
    // Formato novo (N turnos)
    shifts: shifts || [],
    // Formato legado (backward compat — preenchido automaticamente)
    startTimeA: shifts[0]?.start || "",
    endTimeA: shifts[0]?.end || "",
    startTimeB: shifts[1]?.start || "",
    endTimeB: shifts[1]?.end || "",
  };
  return result;
};

/**
 * Inicializa schedules vazios no formato novo.
 */
const createEmptySchedules = () =>
  WEEKDAYS.map((day) => ({
    weekday: day.label(),
    weekdayEn: day.key,
    shifts: [],
    startTimeA: "",
    endTimeA: "",
    startTimeB: "",
    endTimeB: "",
  }));

/**
 * Normaliza schedules recebidos (pode ser legado ou novo).
 */
const normalizeSchedules = (incoming) => {
  if (!isArray(incoming) || incoming.length === 0) {
    return createEmptySchedules();
  }

  return WEEKDAYS.map((day) => {
    const found = incoming.find((s) => s.weekdayEn === day.key);
    if (!found) {
      return {
        weekday: day.label(),
        weekdayEn: day.key,
        shifts: [],
        startTimeA: "",
        endTimeA: "",
        startTimeB: "",
        endTimeB: "",
      };
    }

    const shifts = legacyToShifts(found);
    return shiftsToSchedule(day.key, found.weekday || day.label(), shifts);
  });
};

function SchedulesForm(props) {
  const { initialValues, onSubmit, loading, labelSaveButton } = props;
  const classes = useStyles();

  const [schedules, setSchedules] = useState(createEmptySchedules);

  useEffect(() => {
    if (isArray(initialValues) && initialValues.length > 0) {
      setSchedules(normalizeSchedules(initialValues));
    }
  }, [initialValues]);

  const handleAddShift = useCallback((dayIndex) => {
    setSchedules((prev) => {
      const updated = [...prev];
      const day = { ...updated[dayIndex] };
      const shifts = [...(day.shifts || [])];

      if (shifts.length >= MAX_SHIFTS) return prev;

      shifts.push({ start: "", end: "" });
      updated[dayIndex] = shiftsToSchedule(day.weekdayEn, day.weekday, shifts);
      return updated;
    });
  }, []);

  const handleRemoveShift = useCallback((dayIndex, shiftIndex) => {
    setSchedules((prev) => {
      const updated = [...prev];
      const day = { ...updated[dayIndex] };
      const shifts = [...(day.shifts || [])];

      shifts.splice(shiftIndex, 1);
      updated[dayIndex] = shiftsToSchedule(day.weekdayEn, day.weekday, shifts);
      return updated;
    });
  }, []);

  const handleShiftChange = useCallback((dayIndex, shiftIndex, field, value) => {
    setSchedules((prev) => {
      const updated = [...prev];
      const day = { ...updated[dayIndex] };
      const shifts = [...(day.shifts || [])];

      shifts[shiftIndex] = { ...shifts[shiftIndex], [field]: value };
      updated[dayIndex] = shiftsToSchedule(day.weekdayEn, day.weekday, shifts);
      return updated;
    });
  }, []);

  const handleSubmit = () => {
    // Garantir que o formato de saída inclui tanto shifts quanto legado
    const output = schedules.map((day) => {
      const shifts = (day.shifts || []).filter(
        (s) => s.start && s.end && s.start.length >= 4 && s.end.length >= 4
      );
      return shiftsToSchedule(day.weekdayEn, day.weekday, shifts);
    });
    onSubmit(output);
  };

  return (
    <Formik
      enableReinitialize
      className={classes.fullWidth}
      initialValues={{ schedules }}
      onSubmit={() => setTimeout(() => handleSubmit(), 300)}
    >
      {() => (
        <Form className={classes.fullWidth}>
          <Grid spacing={2} container>
            {schedules.map((day, dayIndex) => (
              <Grid key={day.weekdayEn} xs={12} md={4} item>
                <Paper className={classes.dayCard} elevation={0}>
                  {/* Header do dia */}
                  <div className={classes.dayHeader}>
                    <Typography className={classes.dayName}>
                      {day.weekday}
                    </Typography>
                    <Tooltip title="Adicionar turno">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleAddShift(dayIndex)}
                          disabled={(day.shifts || []).length >= MAX_SHIFTS}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>

                  <Divider className={classes.divider} />

                  {/* Turnos */}
                  {(!day.shifts || day.shifts.length === 0) ? (
                    <Typography className={classes.closedText}>
                      Fechado neste dia
                    </Typography>
                  ) : (
                    day.shifts.map((shift, shiftIndex) => (
                      <div key={shiftIndex} className={classes.shiftRow}>
                        <Typography className={classes.shiftLabel}>
                          Turno {shiftIndex + 1}
                        </Typography>
                        <NumberFormat
                          value={shift.start || ""}
                          variant="outlined"
                          margin="dense"
                          customInput={TextField}
                          format="##:##"
                          placeholder="00:00"
                          style={{ flex: 1 }}
                          label="Início"
                          size="small"
                          onValueChange={(values) =>
                            handleShiftChange(dayIndex, shiftIndex, "start", values.formattedValue)
                          }
                        />
                        <NumberFormat
                          value={shift.end || ""}
                          variant="outlined"
                          margin="dense"
                          customInput={TextField}
                          format="##:##"
                          placeholder="00:00"
                          style={{ flex: 1 }}
                          label="Fim"
                          size="small"
                          onValueChange={(values) =>
                            handleShiftChange(dayIndex, shiftIndex, "end", values.formattedValue)
                          }
                        />
                        <Tooltip title="Remover turno">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveShift(dayIndex, shiftIndex)}
                            style={{ color: "#e53935" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    ))
                  )}

                  {/* Botão adicionar turno (inline) */}
                  {(day.shifts || []).length > 0 && (day.shifts || []).length < MAX_SHIFTS && (
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<AddIcon />}
                      className={classes.addShiftBtn}
                      onClick={() => handleAddShift(dayIndex)}
                    >
                      Adicionar turno
                    </Button>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
          <div className={classes.buttonContainer}>
            <ButtonWithSpinner
              loading={loading}
              type="submit"
              color="primary"
              variant="contained"
            >
              {labelSaveButton ?? i18n.t("whatsappModal.buttons.okEdit")}
            </ButtonWithSpinner>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default SchedulesForm;
